import React from 'react';

/**
 * ApplicationSummary Component
 * Displays the ENTIRE submitted application in a read-only format.
 * Matches the 10-section submission flow, including Section 9 Documents.
 */
const ApplicationSummary = ({ application, settings, onClose }) => {
  if (!application) return null;

  const DetailBlock = ({ label, value, isStatus, fullWidth }) => (
    <div className={`space-y-1 ${fullWidth ? 'col-span-full' : ''}`}>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
      <p className={`text-sm font-bold ${isStatus ? 'text-[#0464AD]' : 'text-slate-900'} leading-relaxed`}>
        {value || 'N/A'}
      </p>
    </div>
  );

  const SectionHeader = ({ title, number }) => (
    <div className="flex items-center gap-4 border-b border-slate-100 pb-2 mb-6 mt-10 first:mt-0">
      <span className="text-2xl font-black text-slate-200">0{number}</span>
      <h4 className="text-xs font-black text-[#0464AD] uppercase tracking-[0.2em]">{title}</h4>
    </div>
  );

  const getUniName = (id) => {
    const uni = (settings?.universities || []).find(u => String(u.id) === String(id));
    return uni ? uni.name : id;
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-4xl rounded-[2.5rem] shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]" 
        onClick={e => e.stopPropagation()}
      >
        {/* Main Header */}
        <div className="bg-[#0464AD] p-8 text-white flex justify-between items-center flex-shrink-0">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tighter">Full Application Record</h2>
            <p className="text-blue-100 text-[10px] font-bold uppercase tracking-widest mt-1">Ref ID: {application.id} | Submitted: {application.dateApplied}</p>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-white/10 rounded-2xl transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        
        <div className="p-12 overflow-y-auto space-y-2 bg-white">
          {/* SECTION 2: PERSONAL */}
          <SectionHeader title="Personal Details" number={2} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <DetailBlock label="First Name" value={application.firstName} />
            <DetailBlock label="Last Name" value={application.lastName} />
            <DetailBlock label="Date of Birth" value={application.dob} />
            <DetailBlock label="Gender" value={application.gender} />
            <DetailBlock label="Email" value={application.email} />
            <DetailBlock label="Phone" value={application.phone} />
            <DetailBlock label="Nationality" value={application.nationality} />
            <DetailBlock label="Address" value={application.streetAddress ? `${application.streetAddress}, ${application.city}, ${application.state} ${application.postcode}, ${application.country}` : 'N/A'} fullWidth />
          </div>

          {/* SECTION 3: PROGRAMS */}
          <SectionHeader title="Program Selections" number={3} />
          <div className="space-y-4">
            {(application.programSelections || []).map((pref, i) => (
              <div key={i} className="flex items-center gap-6 bg-slate-50 p-6 rounded-[2rem] border border-slate-100">
                <span className="text-3xl font-black text-slate-200">#{i+1}</span>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 flex-1">
                  <DetailBlock label="Country" value={pref.country} />
                  <DetailBlock label="University" value={getUniName(pref.universityId)} />
                  <DetailBlock label="TWG Program" value={pref.twgProgram} />
                  <DetailBlock label="Intake" value={pref.intake} />
                  <DetailBlock label="Target Course" value={pref.course} fullWidth />
                </div>
              </div>
            ))}
          </div>

          {/* SECTION 4: GUARDIAN */}
          <SectionHeader title="Parent / Guardian Info" number={4} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <DetailBlock label="Guardian Name" value={`${application.guardianGivenName || ''} ${application.guardianFamilyName || ''}`.trim() || 'N/A'} />
            <DetailBlock label="Relationship" value={application.guardianRelationship} />
            <DetailBlock label="Occupation" value={application.guardianOccupation} />
            <DetailBlock label="Guardian Email" value={application.guardianEmail} />
            {application.guardianAddressSame === 'No' && (
              <DetailBlock label="Guardian Address" value={`${application.guardianStreetAddress}, ${application.guardianCity}, ${application.guardianCountry}`} fullWidth />
            )}
          </div>

          {/* SECTION 5: ACADEMIC */}
          <SectionHeader title="Academic Qualifications" number={5} />
          <div className="space-y-6">
            {/* 1. Academic Qualifications List (Handling multiple entries from the submission flow) */}
            <div className="space-y-4">
              {(application.qualifications || []).map((qual, idx) => (
                <div key={idx} className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                  <p className="text-[10px] font-black text-[#0464AD] mb-4 uppercase tracking-widest">Qualification #{idx + 1}</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    <DetailBlock label="Level" value={qual.highestQualification} />
                    <DetailBlock label="Institution" value={qual.institutionName} />
                    <DetailBlock label="Board/Uni" value={qual.educationBoard} />
                    <DetailBlock label="GPA / Marks" value={qual.marksGpa} />
                    <DetailBlock label="Language" value={qual.languageOfInstruction} />
                    <DetailBlock label="Status" value={qual.isCompleted} />
                    <DetailBlock label="Completion Date" value={qual.completionDate} />
                  </div>
                </div>
              ))}
              
              {/* Fallback for legacy single-entry records */}
              {(!application.qualifications || application.qualifications.length === 0) && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                  <DetailBlock label="Highest Qualification" value={application.highestQualification} />
                  <DetailBlock label="Institution" value={application.institutionName} />
                  <DetailBlock label="Board/Uni" value={application.educationBoard} />
                  <DetailBlock label="Language of Instruction" value={application.languageOfInstruction} />
                  <DetailBlock label="Completed" value={application.isCompleted} />
                  <DetailBlock label="GPA / Marks" value={application.marksGpa} />
                </div>
              )}
            </div>

            {/* 2. Academic Gap Info */}
            {application.hasAcademicGap === 'Yes' && (
              <div className="p-6 bg-amber-50/50 rounded-3xl border border-amber-100">
                <DetailBlock label="Gap Reason" value={application.gapReason} fullWidth />
              </div>
            )}

            {/* 3. NEW: Academic Documents / Transcripts Rendering */}
            {application.academicDocumentsUrls && application.academicDocumentsUrls.length > 0 && (
              <div className="mt-4 p-5 bg-slate-50 rounded-2xl border border-slate-100">
                <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">
                  Academic Documents / Transcripts
                </p>
                <div className="flex flex-wrap gap-4">
                  {application.academicDocumentsUrls.map((url, i) => (
                    <a 
                      key={i} 
                      href={url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs font-bold text-[#0464AD] hover:underline flex items-center gap-2"
                    >
                      <span>📄</span> View Transcript {i + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* SECTION 6: ENGLISH */}
          <SectionHeader title="English Proficiency" number={6} />
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <DetailBlock label="Standardised Test" value={application.hasEnglishTest} />
              <DetailBlock label="Test Name" value={application.englishTestName} />
              <DetailBlock label="Score" value={application.englishTestScore} />
              <DetailBlock label="Verification ID" value={application.englishTestVerificationId} />
              <DetailBlock label="Description" value={application.englishProficiencyDescription} />
            </div>

            {/* English Test Result Documents Display */}
            <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
               <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">English Proficiency Evidence</p>
               {application.englishTestResultUrls && application.englishTestResultUrls.length > 0 ? (
                 <div className="flex flex-wrap gap-4">
                   {application.englishTestResultUrls.map((url, i) => (
                     <a 
                       key={i} 
                       href={url} 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       className="text-xs font-bold text-[#0464AD] hover:underline flex items-center gap-2"
                     >
                       <span>🔤</span> View English Result {i + 1}
                     </a>
                   ))}
                 </div>
               ) : (
                 <p className="text-sm font-bold text-slate-400 italic">No English proficiency documents found.</p>
               )}
            </div>
          </div>

          {/* SECTION 7: FINANCIAL ASSESSMENT */}
          <SectionHeader title="Financial Assessment" number={7} />
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <DetailBlock label="Primary Sponsor" value={application.primarySponsor} />
              <DetailBlock label="Annual Income" value={application.sponsorAnnualIncome} />
              <DetailBlock label="Planned Loan" value={application.plannedLoanAmount} />
              <DetailBlock label="Liquid Funds" value={application.liquidFundsAmount} />
              <DetailBlock label="Asset Value" value={application.assetValue} />
            </div>

            <DetailBlock label="Funding Details" value={application.fundingAdditionalDetails} fullWidth />

            {/* Financial Documents Display */}
            <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
               <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">Supporting Financial Documents</p>
               {application.relevantFinancialDocsUrls && application.relevantFinancialDocsUrls.length > 0 ? (
                 <div className="flex flex-wrap gap-4">
                   {application.relevantFinancialDocsUrls.map((url, i) => (
                     <a 
                       key={i} 
                       href={url} 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       className="text-xs font-bold text-[#0464AD] hover:underline flex items-center gap-2"
                     >
                       <span>💰</span> View Financial Doc {i + 1}
                     </a>
                   ))}
                 </div>
               ) : (
                 <p className="text-sm font-bold text-slate-400 italic">No financial files found.</p>
               )}
            </div>

            {/* Financial Declaration Display */}
            <div className="flex items-center gap-3 px-2">
              <div className={`w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black ${application.financialDeclaration ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                {application.financialDeclaration ? '✓' : '✕'}
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                Financial Capability Declaration {application.financialDeclaration ? 'Confirmed' : 'Not Confirmed'}
              </span>
            </div>
          </div>

          {/* SECTION 8: IMMIGRATION HISTORY */}
          <SectionHeader title="Immigration History" number={8} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <DetailBlock label="Previous Refusal" value={application.visaRefusal} />
            <DetailBlock label="Criminal History" value={application.criminalHistory} />
            <DetailBlock label="Non-Compliant Record" value={application.nonCompliantRecord} />
            <DetailBlock label="Overstay Record" value={application.overstayRecord} />
            <DetailBlock label="Relatives in Country" value={application.relativesInCountry} />
          </div>

          {/* Display detailed explanation if any immigration issues were flagged */}
          {(application.visaRefusal === 'Yes' || 
            application.criminalHistory === 'Yes' || 
            application.nonCompliantRecord === 'Yes' || 
            application.overstayRecord === 'Yes') && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <DetailBlock 
                label="Detailed Immigration Explanation" 
                value={application.immigrationDetails} 
                fullWidth 
              />
            </div>
          )}

          {/* SECTION 9: DOCUMENTS */}
            <SectionHeader title="Uploaded Documents" number={9} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Identification Documents */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                 <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">Identification Documents</p>
                 {application.personalDocUrls && application.personalDocUrls.length > 0 ? (
                   <div className="flex flex-col gap-2">
                     {application.personalDocUrls.map((url, i) => (
                       <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#0464AD] hover:underline flex items-center gap-2">
                         <span>📄</span> View Personal Doc {i + 1}
                       </a>
                     ))}
                   </div>
                 ) : (
                   <p className="text-sm font-bold text-slate-400 italic">No identification files found.</p>
                 )}
              </div>

              {/* Admission Related Documents */}
              <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                 <p className="text-[10px] font-black text-[#0464AD] uppercase tracking-widest mb-3">Admission Related Documents</p>
                 {application.admissionDocUrls && application.admissionDocUrls.length > 0 ? (
                   <div className="flex flex-col gap-2">
                     {application.admissionDocUrls.map((url, i) => (
                       <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-[#0464AD] hover:underline flex items-center gap-2">
                         <span>📄</span> View Admission Doc {i + 1}
                       </a>
                     ))}
                   </div>
                 ) : (
                   <p className="text-sm font-bold text-slate-400 italic">No admission files found.</p>
                 )}
              </div>
            </div>

          {/* SECTION 10: DECLARATIONS */}
          <SectionHeader title="Declarations & Consent" number={10} />
          <div className="bg-slate-900 p-8 rounded-[2rem] text-white grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { l: 'Declaration Agreed', v: application.declarationAgree },
              { l: 'Privacy Policy Accepted', v: application.privacyAgree },
              { l: 'Partner Verification Done', v: application.partnerDeclarationAgree },
              { l: 'Age Above 18', v: application.isAbove18 === 'Yes' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black ${item.v ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/20'}`}>✓</div>
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{item.l}</span>
              </div>
            ))}
            {application.isAbove18 === 'No' && (
              <DetailBlock label="Parent Signature" value={application.guardianSignature} />
            )}
          </div>
        </div>

        <div className="p-8 border-t border-slate-100 bg-slate-50 flex justify-end flex-shrink-0">
          <button onClick={onClose} className="bg-slate-900 text-white px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl hover:bg-slate-800 transition-all active:scale-95">
            Dismiss View
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationSummary;